/**
 * API client for the Field Trips page (sidebar -> Field Trips).
 *
 * Backend: oh_operations Django app, alfred-suffixed endpoints under
 * /api/v1/oh_operations/field_trip/. Venue, date, grade and status are real
 * columns; the rough plan (logistics, teachers, volunteers, attendees, the
 * time-wise breakdown, the safety checklist) travels in `data`.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

// One row per trip for a centre ("all" = every centre the user may see).
const fetchFieldTrips = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.fieldTrips}/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching field trips')
    throw error
  }
}

// The full trip including its plan.
const fetchFieldTrip = async (uuid) => {
  try {
    const response = await get(`${apiEndpoints.fieldTrips}/${uuid}/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching field trip')
    throw error
  }
}

// A centre's staff (Team Wall roster) and preschool children with the parent's
// name and number — what the form's search boxes pick from.
const fetchFieldTripPeople = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.fieldTrips}/people/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching field trip people')
    throw error
  }
}

const createFieldTrip = async (payload) => {
  try {
    const response = await post(`${apiEndpoints.fieldTrips}/alfred`, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating field trip')
    throw error
  }
}

const updateFieldTrip = async (uuid, payload) => {
  try {
    const response = await patch(
      `${apiEndpoints.fieldTrips}/${uuid}/alfred`,
      payload
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating field trip')
    throw error
  }
}

// Marks the trip complete and locks it. One-way — there is no re-open.
const completeFieldTrip = async (uuid, closing) => {
  try {
    const response = await post(
      `${apiEndpoints.fieldTrips}/${uuid}/complete/alfred`,
      { closing }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in completing field trip')
    throw error
  }
}

const deleteFieldTrip = async (uuid) => {
  try {
    await remove(`${apiEndpoints.fieldTrips}/${uuid}/alfred`)
    return uuid
  } catch (error) {
    sentryLogger(error, 'error in deleting field trip')
    throw error
  }
}

export {
  fetchFieldTrips,
  fetchFieldTrip,
  fetchFieldTripPeople,
  createFieldTrip,
  updateFieldTrip,
  completeFieldTrip,
  deleteFieldTrip
}
